'use client';

import React from 'react';
import styled from 'styled-components';
import { focusRing } from '@liftango/ui-tokens';

export interface ErrorStateProps {
  title: React.ReactNode;
  body?: React.ReactNode;
  /** When provided, renders a Retry button that calls this on click. */
  onRetry?: () => void;
  /** Override the retry button label (defaults to "Retry"). */
  retryLabel?: string;
  /** Additional action node rendered alongside the retry button. */
  action?: React.ReactNode;
  className?: string;
}

const Surface = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: ${(p) => p.theme.space[8]} ${(p) => p.theme.space[6]};
  gap: ${(p) => p.theme.space[2]};
`;

const Mark = styled.div`
  width: 36px;
  height: 36px;
  border-radius: ${(p) => p.theme.radius.pill};
  background: ${(p) => p.theme.colors.semantic.error.soft};
  color: ${(p) => p.theme.colors.semantic.error.color};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 700;
  margin-bottom: ${(p) => p.theme.space[2]};
`;

const Title = styled.div`
  font-family: ${(p) => p.theme.fontFamily.ui};
  font-size: ${(p) => p.theme.fontSize.body};
  font-weight: ${(p) => p.theme.fontWeight.semibold};
  color: ${(p) => p.theme.colors.neutral.ink};
`;

const Body = styled.div`
  font-family: ${(p) => p.theme.fontFamily.ui};
  font-size: ${(p) => p.theme.fontSize.sm};
  color: ${(p) => p.theme.colors.neutral.inkSoft};
  line-height: 1.55;
  max-width: 44ch;
`;

const Actions = styled.div`
  margin-top: ${(p) => p.theme.space[3]};
  display: inline-flex;
  gap: ${(p) => p.theme.space[2]};
`;

const Retry = styled.button`
  height: 28px;
  padding: 0 12px;
  border-radius: ${(p) => p.theme.radius.lg};
  background: ${(p) => p.theme.colors.neutral.panel};
  color: ${(p) => p.theme.colors.neutral.ink};
  border: 1px solid ${(p) => p.theme.colors.neutral.line};
  font-family: ${(p) => p.theme.fontFamily.ui};
  font-size: ${(p) => p.theme.fontSize.sm};
  font-weight: ${(p) => p.theme.fontWeight.semibold};
  cursor: pointer;

  &:hover {
    border-color: ${(p) => p.theme.colors.neutral.lineStrong};
    background: ${(p) => p.theme.colors.neutral.panelAlt};
  }
  ${focusRing}
`;

/**
 * "Something went wrong" placeholder for failed loads. Use within a
 * region that owns its own load lifecycle (a panel, a drawer, a card).
 * For top-level error pages use a route-level component.
 */
export function ErrorState({
  title,
  body,
  onRetry,
  retryLabel = 'Retry',
  action,
  className,
}: ErrorStateProps) {
  return (
    <Surface role="alert" className={className}>
      <Mark aria-hidden="true">!</Mark>
      <Title>{title}</Title>
      {body !== undefined && <Body>{body}</Body>}
      {(onRetry !== undefined || action !== undefined) && (
        <Actions>
          {onRetry !== undefined && (
            <Retry type="button" onClick={onRetry}>
              {retryLabel}
            </Retry>
          )}
          {action}
        </Actions>
      )}
    </Surface>
  );
}
